/**
 * Focus Styles Configuration
 * Standardized focus indicators for accessibility
 *
 * WCAG Reference: 2.4.7 Focus Visible
 *
 * Design Principles:
 * - 2px outline using operational blue for visibility
 * - 2px offset to avoid overlap with element borders
 * - Only show on keyboard navigation (focus-visible)
 * - High contrast mode support
 */

import { SxProps, Theme } from '@mui/material/styles';

/**
 * Focus color - Mensova operational blue for dark-surface clarity
 */
export const FOCUS_COLOR = '#6F91B3';

/**
 * Focus outline width
 */
export const FOCUS_WIDTH = 2;

/**
 * Focus outline offset
 */
export const FOCUS_OFFSET = 2;

/**
 * Standard focus outline styles
 * Use for buttons, links, and most interactive elements
 */
export const focusOutline: SxProps<Theme> = {
  outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
  outlineOffset: `${FOCUS_OFFSET}px`,
};

/**
 * Inset focus styles
 * Use for elements where offset would cause layout issues (tabs, menu items)
 */
export const focusInset: SxProps<Theme> = {
  outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
  outlineOffset: `-${FOCUS_OFFSET}px`,
};

/**
 * Focus ring using box-shadow (for rounded elements)
 * Use when outline doesn't follow border-radius properly
 */
export const focusRing: SxProps<Theme> = {
  outline: 'none',
  boxShadow: `0 0 0 ${FOCUS_WIDTH}px ${FOCUS_COLOR}`,
};

/**
 * Focus background highlight
 * Use for menu items, list items where outline isn't appropriate
 */
export const focusHighlight: SxProps<Theme> = {
  outline: 'none',
  backgroundColor: 'rgba(111, 145, 179, 0.14)',
};

/**
 * Focus style variant type
 */
export type FocusStyleVariant = 'standard' | 'inset' | 'ring' | 'highlight' | 'within';

/**
 * Focus styles object for component overrides
 * Applies to :focus-visible pseudo-class
 */
export const focusStyles: Record<FocusStyleVariant, Record<string, unknown>> = {
  /**
   * Standard focus outline
   * Use for: buttons, links, icon buttons, chips
   */
  standard: {
    '&:focus-visible': {
      outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
      outlineOffset: `${FOCUS_OFFSET}px`,
    },
    // Remove focus on mouse click
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
  },

  /**
   * Inset focus outline
   * Use for: tabs, contained elements
   */
  inset: {
    '&:focus-visible': {
      outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
      outlineOffset: `-${FOCUS_OFFSET}px`,
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
  },

  /**
   * Focus ring (box-shadow based)
   * Use for: inputs, rounded buttons, cards
   */
  ring: {
    '&:focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 ${FOCUS_WIDTH}px ${FOCUS_COLOR}`,
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
      boxShadow: 'none',
    },
  },

  /**
   * Background highlight focus
   * Use for: menu items, list items, table rows
   */
  highlight: {
    '&:focus-visible': {
      outline: 'none',
      backgroundColor: 'rgba(111, 145, 179, 0.14)',
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
      backgroundColor: 'transparent',
    },
  },

  /**
   * Focus within container
   * Use for: form groups, card containers
   */
  within: {
    '&:focus-within': {
      outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
      outlineOffset: `${FOCUS_OFFSET}px`,
    },
  },
};

/**
 * Input focus styles
 * Special handling for text inputs with existing borders
 */
export const inputFocusStyles = {
  '&.Mui-focused': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: FOCUS_COLOR,
      borderWidth: `${FOCUS_WIDTH}px`,
    },
  },
  '&:focus-within': {
    outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
    outlineOffset: `${FOCUS_OFFSET}px`,
    borderRadius: 4,
  },
};

/**
 * High contrast mode styles
 * Enhances focus visibility for users with prefers-contrast: more
 */
export const highContrastFocus = {
  '@media (prefers-contrast: more)': {
    '&:focus-visible': {
      outline: '3px solid',
      outlineOffset: '3px',
    },
  },
};

/**
 * Global CSS for focus styles
 * Include this in your global styles or CSS file
 */
export const globalFocusCSS = `
/* Default focus-visible styles */
:focus-visible {
  outline: ${FOCUS_WIDTH}px solid ${FOCUS_COLOR};
  outline-offset: ${FOCUS_OFFSET}px;
}

/* Remove focus on mouse click */
:focus:not(:focus-visible) {
  outline: none;
}

/* High contrast mode support */
@media (prefers-contrast: more) {
  :focus-visible {
    outline: 3px solid;
    outline-offset: 3px;
  }
}

/* Reduced motion - instant focus */
@media (prefers-reduced-motion: reduce) {
  :focus-visible {
    transition: none;
  }
}
`;

/**
 * Helper to create custom focus styles
 */
export function createFocusStyles(options: {
  color?: string;
  width?: number;
  offset?: number;
  style?: 'outline' | 'ring' | 'highlight';
}): SxProps<Theme> {
  const {
    color = FOCUS_COLOR,
    width = FOCUS_WIDTH,
    offset = FOCUS_OFFSET,
    style = 'outline',
  } = options;

  switch (style) {
    case 'ring':
      return {
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 ${width}px ${color}`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
          boxShadow: 'none',
        },
      };
    case 'highlight':
      return {
        '&:focus-visible': {
          outline: 'none',
          backgroundColor: `${color}1F`, // 12% opacity
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      };
    default:
      return {
        '&:focus-visible': {
          outline: `${width}px solid ${color}`,
          outlineOffset: `${offset}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      };
  }
}
